import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { customEncode, getRolesAndPermission } from '@piqet-angular/data';
import { ICellRendererParams } from 'ag-grid-community';
import {
  AccessPermissionService,
  clickTooltipDef,
  mainModuleName,
  rolesModuleName,
} from '@piqet-angular/shared';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'piqet-angular-grid-project-link',
  templateUrl: './grid-project-link.component.html',
  styleUrls: ['./grid-project-link.component.css'],
})
export class GridProjectLinkComponent {
  params: any;
  activatedRoute!: string;
  clickTooltipDef = clickTooltipDef;

  columnConfig = {
    isProjectName: false,
    isProductName: false,
    isDashboardData: false,
  };

  accessConfig = {
    showProjectEdit: false,
    showProductEdit: false,
    checkProjectAccess: false,
    checkProductAccess: false,
    checkAllProjectDashboardAccess: false,
    checkAllProductDashboardAccess: false,
  };

  constructor(
    private route: Router,
    private router: ActivatedRoute,
    private accessService: AccessPermissionService,
    private toaster: ToastrService
  ) {}

  agInit(params: ICellRendererParams<any, any>): void {
    this.params = params;
    this.activatedRoute = this.router.snapshot.routeConfig?.path || '';
    this.initializeColumnConfig(params);
    this.initializeAccessConfig();
  }

  private initializeColumnConfig(params: any): void {
    this.columnConfig = {
      isProjectName: params.colDef.colId === 'ProjectName',
      isProductName: params.colDef.colId === 'ProductName',
      isDashboardData: params.colDef.cellRendererParams.projectName === 'dashboard',
    };
  }

  private initializeAccessConfig(): void {
    const packagingAccess = this.isAccess(this.activatedRoute, 'packaging');
    const productAccess = this.isAccess(this.activatedRoute);

    this.accessConfig = {
      showProjectEdit: this.accessService.hasPermission('edit', this.activatedRoute, 'packaging'),
      showProductEdit: this.accessService.hasPermission('edit', this.activatedRoute),
      checkProjectAccess: packagingAccess ? packagingAccess.isView || packagingAccess.isEdit : true,
      checkProductAccess: productAccess ? productAccess.isView || productAccess.isEdit : true,
      checkAllProjectDashboardAccess: false,
      checkAllProductDashboardAccess: false,
    };
  }

  private handleUnauthorizedAccess(): void {
    this.toaster.error('Unauthorized access. Please contact the administrator');
  }

  private navigateToProduct(): void {
    if (this.accessConfig.checkProductAccess) {
      const encodedValue = this.params.data.productConfigId;
      localStorage.setItem('productConfigId', encodedValue);
      this.route.navigate([
        `projects/product-configuration/${customEncode(btoa(encodedValue))}/setting`,
      ]);
    } else {
      this.handleUnauthorizedAccess();
    }
  }

  private navigateToProject(): void {
    if (this.accessConfig.checkProjectAccess) {
      const encodedValue = encodeURIComponent(this.params.data.projectId);
      localStorage.setItem('pCode', this.params.data.projectId);
      this.route.navigate([
        `projects/projects-setting/${customEncode(btoa(encodedValue))}/setting`,
      ]);
    } else {
      this.handleUnauthorizedAccess();
    }
  }

  private handleDashboardNavigation(): void {
    const isProductProject = this.params.data.projectType === 'Product project';
    const access = this.isAccess(this.activatedRoute, isProductProject ? undefined : 'packaging');

    if (isProductProject) {
      this.accessConfig.checkAllProductDashboardAccess = access.isEdit || access.isView;
      this.accessConfig.checkAllProductDashboardAccess
        ? this.navigateToProduct()
        : this.handleUnauthorizedAccess();
    } else {
      this.accessConfig.checkAllProjectDashboardAccess = access.isEdit || access.isView;
      this.accessConfig.checkAllProjectDashboardAccess
        ? this.navigateToProject()
        : this.handleUnauthorizedAccess();
    }
  }

  routeToProduct(): void {
    this.navigateToProduct();
  }

  routeTo(): void {
    if (this.activatedRoute === 'dashboard') {
      this.handleDashboardNavigation();
    } else {
      this.navigateToProject();
    }
  }

  isAccess = (pathName: string, type?: string) => {
    return pathName === 'admin-projects'
      ? getRolesAndPermission(
          rolesModuleName.AdministratorProjects,
          mainModuleName.projectManagementModule
        )
      : getRolesAndPermission(
          type ? rolesModuleName.packageScenario : rolesModuleName.productScenario,
          type ? mainModuleName.projectsModule : mainModuleName.productsModule
        );
  };
}
